import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ClipboardCheck, UserCheck, Layers, ArrowRight, ArrowDown } from "lucide-react";
import { MaskedLine, EASE } from "../Reveal";
import { scrollToAnchor } from "../Header";

const HERO_IMG =
  "https://images.unsplash.com/photo-1614595737476-42487331b8a1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MTN8MHwxfHNlYXJjaHw0fHxhcmNoaXRlY3R1cmFsJTIwY29uY3JldGUlMjBzdG9uZSUyMHRpbWJlciUyMGZhY2FkZSUyMG1hdGVyaWFsc3xlbnwwfHx8fDE3ODk3MTczMDl8MA&ixlib=rb-4.1.0&q=85";

const REASSURANCES = [
  { icon: ClipboardCheck, label: "Étude personnalisée", testid: "reassurance-etude" },
  { icon: UserCheck, label: "Interlocuteur dédié", testid: "reassurance-interlocuteur" },
  { icon: Layers, label: "Solutions multi-gammes", testid: "reassurance-gammes" },
];

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yImg = useTransform(scrollYProgress, [0, 1], [0, 130]);
  const yText = useTransform(scrollYProgress, [0, 1], [0, -50]);

  return (
    <section
      id="accueil"
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-screen bg-ivoire grain overflow-hidden flex items-center pt-[72px]"
    >
      <div
        className="absolute -top-24 -right-24 w-[420px] h-[420px] rounded-t-full bg-sagelight/70 rotate-90"
        aria-hidden="true"
      />
      <div
        className="absolute bottom-0 left-0 w-40 h-40 rounded-t-full bg-sable/60"
        aria-hidden="true"
      />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 w-full py-16 lg:py-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
          <motion.div style={{ y: yText }} className="lg:col-span-7">
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.1 }}
              className="text-xs uppercase tracking-[0.3em] font-semibold text-teal mb-8"
              data-testid="hero-overline"
            >
              Négoce de matériaux — Construction &amp; rénovation
            </motion.p>
            <h1
              className="font-display font-medium text-petrol text-5xl sm:text-6xl lg:text-7xl xl:text-[5.2rem] leading-[1.02] tracking-tight"
              data-testid="hero-title"
            >
              <MaskedLine delay={0.25}>Vos matériaux.</MaskedLine>
              <MaskedLine delay={0.42} className="pb-3">
                <span className="italic text-teal">Au bon endroit.</span>
              </MaskedLine>
            </h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.7, ease: EASE }}
              className="mt-7 text-base sm:text-lg text-mute max-w-xl leading-relaxed"
              data-testid="hero-subtitle"
            >
              <span className="text-encre font-medium">
                Matériaux de construction et de rénovation pour les professionnels.
              </span>
              <br />
              Présentez votre besoin à EQUORIS : nous qualifions votre demande et vous
              proposons une réponse commerciale adaptée à votre projet.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.9, ease: EASE }}
              className="mt-9 flex flex-col sm:flex-row gap-4"
            >
              <button
                onClick={() => scrollToAnchor("votre-besoin")}
                data-testid="hero-cta-commercial"
                className="group rounded-full bg-petrol text-ivoire text-sm font-semibold px-7 py-4 inline-flex items-center justify-center gap-2 transition-all duration-300 hover:bg-teal hover:-translate-y-0.5"
              >
                Demander le passage d'un commercial
                <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
              </button>
              <button
                onClick={() => scrollToAnchor("materiaux")}
                data-testid="hero-cta-materiaux"
                className="rounded-full border border-petrol/25 text-petrol text-sm font-semibold px-7 py-4 inline-flex items-center justify-center gap-2 transition-all duration-300 hover:border-teal hover:text-teal hover:-translate-y-0.5"
              >
                Découvrir nos matériaux
                <ArrowDown className="w-4 h-4" />
              </button>
            </motion.div>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 1, delay: 1.15 }}
              className="mt-12 flex flex-wrap gap-x-8 gap-y-3"
            >
              {REASSURANCES.map(({ icon: Icon, label, testid }) => (
                <div key={testid} data-testid={testid} className="flex items-center gap-2.5">
                  <span className="w-8 h-8 rounded-full bg-sagelight flex items-center justify-center">
                    <Icon className="w-4 h-4 text-teal" />
                  </span>
                  <span className="text-[13px] font-medium text-encre/75">{label}</span>
                </div>
              ))}
            </motion.div>
          </motion.div>

          <div className="lg:col-span-5 relative">
            <motion.div
              initial={{ opacity: 0, y: 60, scale: 0.97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ duration: 1.3, delay: 0.55, ease: EASE }}
              className="relative"
            >
              <motion.div style={{ y: yImg }} className="relative">
                <div className="rounded-t-[999px] rounded-b-[2rem] overflow-hidden shadow-2xl shadow-petrol/20 border border-petrol/10">
                  <img
                    src={HERO_IMG}
                    alt="Façade architecturale contemporaine en béton, pierre et bois"
                    className="w-full h-[420px] sm:h-[500px] lg:h-[560px] object-cover"
                    loading="eager"
                    data-testid="hero-image"
                  />
                </div>
                <div
                  className="absolute -bottom-6 -left-6 sm:-left-10 bg-ivoire/95 backdrop-blur-md border border-petrol/10 rounded-2xl px-5 py-4 shadow-lg"
                  data-testid="hero-badge"
                >
                  <p className="font-mono text-[10px] tracking-[0.2em] text-teal uppercase mb-1">
                    EQUORIS
                  </p>
                  <p className="text-sm font-medium text-petrol">
                    Conseil · Sélection · Accompagnement
                  </p>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};
